import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "config/api";
import { MdOutlineLogout, MdOutlineCheckCircle } from "react-icons/md";
import { FaStripe } from "react-icons/fa6";

export default function StripeSection() {
  const [stripeData, setStripeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [connectError, setConnectError] = useState(false);

  // ✅ Stripe 데이터 불러오기
  const fetchStripeData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No token found");

      const response = await axios.get(`${API_BASE_URL}/api/users/stripe/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) setStripeData(response.data);
    } catch (error) {
      console.error("Failed to fetch Stripe account data:", error);
      setConnectError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStripeData();
  }, []);

  // ✅ Stripe 연결
  const handleConnectStripe = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No token found");

      // 동의 로그 남기기
      await axios.post(
        `${API_BASE_URL}/api/payments/consent/`,
        { platform: "stripe", agreed: true, version: "v1.0" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // OAuth 요청
      const response = await axios.get(`${API_BASE_URL}/api/users/stripe/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 && response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Failed to connect to Stripe:", error);
    }
  };

  // ✅ 동기화 (Square와 동일하게)
  const handleSyncStripeProducts = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/api/payments/stripe/sync-stripe-products/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        alert("✅ Stripe products synced successfully!");
        fetchStripeData(); // UI 갱신
      }
    } catch (error) {
      console.error("Failed to sync Stripe products:", error);
      alert("❌ Failed to sync Stripe products. Check logs.");
    }
  };

  // ✅ 연결 해제
  const handleDisconnectStripe = async () => {
    if (!window.confirm("Disconnect Stripe?")) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/api/payments/disconnect/`,
        { platform: "stripe" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStripeData(null);
    } catch (error) {
      console.error("Failed to disconnect Stripe:", error);
    }
  };

  // ✅ 로딩 UI
  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <span className="text-gray-400 text-sm">Loading Stripe info...</span>
      </div>
    );
  }

  // ✅ UI
  return (
    <div className="flex items-center justify-between bg-neutral-950 border border-neutral-800 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-3">
        <FaStripe className="text-blue-500 text-3xl" />
        <div>
          <p className="text-lg font-medium">Stripe</p>
          <p className="text-gray-400 text-sm">
            Connect your Stripe account to sync products and sales data.
          </p>
        </div>
      </div>

      {stripeData?.connected && !connectError ? (
        <div className="flex items-center gap-3">
          <MdOutlineCheckCircle className="text-green-400 text-xl" />
          <button
            onClick={handleSyncStripeProducts}
            className="bg-blue-500 hover:bg-blue-400 px-3 py-1.5 rounded-md text-sm font-medium"
          >
            Sync Products
          </button>
          <button
            onClick={handleDisconnectStripe}
            className="flex items-center gap-1 bg-neutral-800 hover:bg-neutral-700 px-3 py-1.5 rounded-md text-sm"
          >
            <MdOutlineLogout /> Disconnect
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-end gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="accent-blue-500"
            />
            I agree to connect my Stripe account.
          </label>
          <button
            onClick={handleConnectStripe}
            disabled={!agree}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              agree
                ? "bg-blue-500 hover:bg-blue-400"
                : "bg-gray-600 cursor-not-allowed"
            }`}
          >
            Connect Stripe
          </button>
        </div>
      )}
    </div>
  );
}